import { FC, useEffect } from "react";

import notification from "antd/es/notification";
import { RcFile } from "antd/es/upload/interface";
import { defineMessages, useIntl } from "react-intl";

const messages = defineMessages({
  error: {
    id: "instantUploader.error",
    defaultMessage:
      "File {notSupported, select, true {not supported{tooLarge, select, true { and } other {}}} other {}}{tooLarge, select, true {too large} other {}}. Please try again.",
    description: "Upload error",
  },
});

type BeforeUploadValueType = Blob | boolean | File | string | void;

export interface InstantUploaderProps {
  allowedMimeTypes?: string[];
  beforeUpload?: (
    file: RcFile,
    FileList: RcFile[]
  ) => BeforeUploadValueType | Promise<BeforeUploadValueType>;
  onHide: () => void;
  onUpload?: (file: File) => void;
  sizeLimit?: number;
}

// This component is intended to be used within the ImgCrop. If not that, we wouldn't
// need this logic as a separate component. We only mimic the props (beforeUpload)
// that ImgCrop expects.
const InstantUploader: FC<InstantUploaderProps> = ({
  allowedMimeTypes,
  beforeUpload,
  onHide,
  onUpload,
  sizeLimit,
}) => {
  const intl = useIntl();

  useEffect(
    () => {
      const input = document.createElement("input");
      input.type = "file";
      input.oncancel = function () {
        onHide();
        input.remove();
      };
      input.onchange = function () {
        if (!input.files) {
          onHide();
          input.remove();
          return;
        }

        const file = input.files[0];
        if (!file) {
          onHide();
          input.remove();
          return;
        }

        const notSupported = allowedMimeTypes ? !allowedMimeTypes.includes(file.type) : false;
        const tooLarge = sizeLimit !== undefined ? file.size >= sizeLimit : false;
        if (notSupported || tooLarge) {
          notification.warn({
            message: intl.formatMessage(messages.error, {
              notSupported,
              tooLarge,
            }),
          });
          return;
        }

        const result = beforeUpload?.(file as RcFile, []);
        Promise.all([result])
          .then(([result]) => {
            if (result instanceof File) {
              onUpload?.(result);
            }
          })
          .finally(() => {
            onHide();
            input.remove();
          });
      };

      input.click();
    },
    // We don't put beforeUpload here for a reason, as ImgCrop keeps sending new value
    // which makes new Open File dialog to appear again and again
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowedMimeTypes, intl, onHide, onUpload, sizeLimit]
  );

  return null;
};

export default InstantUploader;
